/**
 * Copyright (c) Microsoft. All rights reserved.
 */
import { ResourceAssignmentDto } from 'src/core/services/resourceAssignmentSwaggers';
import { getLogger } from 'src/core/services/telemetry/telemetryLogger';
import { AzureCsAccountKind } from 'src/core/stores/AzureCsAccountStore';
import { TelemetryFeatureTitle } from 'src/core/types/telemetryTypes';
import { DTJobCreationStepName, DTPageName, getFeatureNameFromTitle, getServiceNameFromTitle } from 'src/core/utils/telemetryUtils';
import { CardType } from 'src/languageStudio/appShell/landingPage/components/PrebuiltDemoCard';

/**
 * Logs the current region
 *
 * @param region the name of the current region
 */
export const logRegion = (region: string) => getLogger().logEvent(`Region:${region}`, { isUserAction: false });

export const logResourceCreation = (resourceType: AzureCsAccountKind) =>
    getLogger().logEvent('chooseLanguageResourceModal:Button:createNewResource:Click', { isUserAction: true, resourceType });

export const logFreeAccountCreation = () =>
    getLogger().logEvent('chooseLanguageResourceModal:ExternalLink:createAFreeAccount', { isUserAction: true });

export const logLandingPageProjectCreation = (featureTitle: TelemetryFeatureTitle) =>
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:LandingPage:Button:CreateProjectFromLandingPage`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });

/**
 * Logs the type of the created project
 *
 * @param projectType the type of the crated project
 */
export const logProjectCreated = (projectType: TelemetryFeatureTitle, projectId: string) =>
    getLogger().logEvent(`${getFeatureNameFromTitle(projectType)}:Project:Create`, {
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(projectType)
    });

/**
 * Logs the type of the clicked project
 *
 * @param projectType the type of the crated project
 * @param pageName the name of the current page
 * @param projectId the id of the clicked project
 */
export const logProjectClicked = (projectType: TelemetryFeatureTitle, pageName: string, projectId: string) =>
    getLogger().logEvent(`${getFeatureNameFromTitle(projectType)}:${pageName}:Project:Click`, {
        projectId,
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(projectType)
    });

/**
 * Logs the name of the locale chosen by the user
 *
 * @param localeName the name of the current locale
 */
export const logLocaleChanged = (localeName: string) =>
    getLogger().logEvent(`LocalizedLanguage:${localeName}:Select`, { isUserAction: true });

/**
 * Logs when a user uses Jump start method
 */
export const logJumpStart = () => getLogger().logEvent(`JumpStart`, { isUserAction: true });

/**
 * Logs when a user clicks a pivot in the landing page
 * @param pivotName the name of the pivot
 */
export const logLandingPagePivotClick = (pivotName: string) =>
    getLogger().logEvent(`LandingPagePivot:${pivotName}:Click`, { isUserAction: true });

/**
 * Logs when a user sign
 */
export const logSignIn = () => getLogger().logEvent(`Signin:Button:Click`, { isUserAction: true });

/**
 * Logs when the user successfully
 * complete the function then click on nextSteps
 *
 */
export const logNextSteps = () => getLogger().logEvent(`NextSteps:Click`, { isUserAction: true });

/**
 * Logs that the user doesn't have Language resource
 */
export const logLanguageResourceNotFound = () => getLogger().logEvent(`LandingPage:LanguageResource:NotFound`, { isUserAction: false });

/**
 * Logs that the user doesn't have Microsoft subscription
 */
export const logMicrosoftSubscriptionNotFound = () =>
    getLogger().logEvent(`LandingPage:MicrosoftSubscription:NotFound`, { isUserAction: false });

/**
 * Logs when the user reaches
 * the bottom of the landing page
 */
export const logLandingPageBottomReached = () => getLogger().logEvent('LandingPageBottom:Reached', { isUserAction: true });

/**
 * Logs the name of the clicked landing-page card
 *
 * @param cardType the type of the card
 * @param featureTitle the name of the card
 */
export const logDemoCard = (cardType: CardType, featureTitle: TelemetryFeatureTitle) =>
    getLogger().logEvent(`${cardType}:${getFeatureNameFromTitle(featureTitle)}:cardClicked`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });

export const logTileCardClick = (demoName: TelemetryFeatureTitle, tileCardName: TelemetryFeatureTitle) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(demoName)}:TileClick:${getFeatureNameFromTitle(tileCardName)}`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(demoName)
    });
};
/**
 * Logs when a user create a new
 * language resource
 */
export const logLanguageResourceCreated = (resourceType: AzureCsAccountKind) =>
    getLogger().logEvent('LanguageResource:Create', { isUserAction: true, resourceType });

/**
 * Logs when a user dismiss onboarding wizard
 */
export const logWizardDismiss = () => getLogger().logEvent('Wizard:Dismiss', { isUserAction: true });

/**
 * Logs when a user click on any learn more link
 * on landing page
 */

export const logURlLinkClick = (featureTitle: TelemetryFeatureTitle, pageName: string, urlName: string, projectId?: string) =>
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:UrlClick:${urlName}`, {
        projectId,
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });

/**
 * Logs when a user open Intents filters
 */
export const logIntentsFiltersPaneOpened = (featureTitle: TelemetryFeatureTitle) =>
    getLogger().logEvent('IntentsFilters:Open', { isUserAction: true, serviceName: getServiceNameFromTitle(featureTitle) });

/**
 * Logs when a user close intents filters
 */
export const logIntentsFiltersPaneClosed = (featureTitle: TelemetryFeatureTitle) =>
    getLogger().logEvent('IntentsFilters:Close', { isUserAction: true, serviceName: getServiceNameFromTitle(featureTitle) });

/**
 * Logs when a user click on Smiley
 */

export const logCustomerFeedbackSmileyClick = (featureTitle: TelemetryFeatureTitle) =>
    getLogger().logEvent('CustomerFeedback:Smiley:Click', { isUserAction: true, serviceName: getServiceNameFromTitle(featureTitle) });

/**
 * Logs the type of the labelEntity
 *
 * @param type of the created label entity
 */
export const logLabelEntity = (type: string, featureTitle: TelemetryFeatureTitle) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:LabelEntity:${type}`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logCommandBarButtons = (featureTitle: TelemetryFeatureTitle, buttonName: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:Button:${buttonName}`, {
        serviceName: getServiceNameFromTitle(featureTitle),
        isUserAction: true
    });
};

export const logRunModelClicked = (featureTitle: TelemetryFeatureTitle) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:Model:Run`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logSampleClick = (featureTitle: TelemetryFeatureTitle, sampleTitle: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:Content:Select:${sampleTitle}`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logAcknowledgementClicked = (featureTitle: TelemetryFeatureTitle) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:Acknowledge:Select`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logDragAndDropFiles = (featureTitle: TelemetryFeatureTitle) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:Content:Upload`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logDemoCustomContent = (featureTitle: TelemetryFeatureTitle) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:Content:CustomEntry`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logFeaturesTextLanguage = (featureTitle: TelemetryFeatureTitle, language: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:LanguageSelection:Select`, {
        selectedLanguage: language,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logFeaturesResouceSelection = (featureTitle: TelemetryFeatureTitle, resource: string, pageName?: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:ResourceSelection:Select`, {
        pageName,
        isUserAction: true,
        selectedResource: resource,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logDocumentLanguage = (featureTitle: TelemetryFeatureTitle, pageName: string, language: string, projectId: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:LanguageSelection:Select`, {
        selectedLanguage: language,
        projectId,
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logTextEntry = (featureTitle: TelemetryFeatureTitle, pageName: string, entryType: string, projectId?: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:Content:${entryType}`, {
        projectId,
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logInputPivotClick = (featureTitle: TelemetryFeatureTitle, pivotName: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:DocumentInput:${pivotName}:Pivot:Click`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logJsonPivotClicked = (featureTitle: TelemetryFeatureTitle) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:JsonPivot:Click`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logVisualizationInteraction = (featureTitle: TelemetryFeatureTitle, actionName: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:VisualizationInteraction:${actionName}`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logDemoButtonClick = (
    featureTitle: TelemetryFeatureTitle,
    buttonPlace: string,
    buttonName: string,
    pivotName?: string,
    projectId?: string
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${buttonPlace}:Button:${buttonName}`, {
        pivotName,
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logButtonClick = (
    featureTitle: TelemetryFeatureTitle,
    buttonPlace: string,
    buttonName: string,
    pivotName?: string,
    projectId?: string
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${buttonPlace}:Button:${buttonName}`, {
        pivotName,
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logQnaKBButtonClick = (
    featureTitle: TelemetryFeatureTitle,
    buttonPlace: string,
    section: string,
    action: string,
    pivotName?: string,
    projectId?: string
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:NewEditKb:${buttonPlace}:${section}:${action}`, {
        pivotName,
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logButtonSuccessfulClick = (
    featureTitle: TelemetryFeatureTitle,
    buttonPlace: string,
    buttonName: string,
    pivotName?: string,
    projectId?: string
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${buttonPlace}:Button:${buttonName}:Succeeded`, {
        pivotName,
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logButtonSuccessfulResourceAssignmentClick = (
    featureTitle: TelemetryFeatureTitle,
    buttonPlace: string,
    buttonName: string,
    pivotName?: string,
    projectId?: string,
    assignmentData?: ResourceAssignmentDto
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${buttonPlace}:Button:${buttonName}:Succeeded`, {
        pivotName,
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle),
        regionId: assignmentData?.id,
        customDomain: assignmentData?.customDomain,
        subscriptionId: assignmentData?.subscriptionId,
        azureResourceId: assignmentData?.azureResourceId
    });
};

export const logAutoLabelCreate = (featureTitle: TelemetryFeatureTitle, projectId: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:AutoLabel:Create:AutoLabeling`, {
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logGptCreate = (featureTitle: TelemetryFeatureTitle, projectId: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:AutoLabel:Create:GptLabeling`, {
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logNextWizardStep = (featureTitle: TelemetryFeatureTitle, pageName: string, stepName: string, actionName: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:Wizard:Step${stepName}:${actionName}:Click`, {
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logFilterClick = (
    featureTitle: TelemetryFeatureTitle,
    buttonPlace: string,
    buttonName: string,
    pivotName?: string,
    projectId?: string
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${buttonPlace}:Filter:${buttonName}`, {
        pivotName,
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logSourceFilterClick = (featureTitle: TelemetryFeatureTitle, buttonPlace: string, pivotName?: string, projectId?: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${buttonPlace}:Source:select`, {
        pivotName,
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logSelectLanguageInProjectCreation = (featureTitle: TelemetryFeatureTitle, language: string, projectId: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:CreateProject:LanguageSelection:Select`, {
        projectLanguage: language,
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logMultiLingualInProjectCreation = (featureTitle: TelemetryFeatureTitle, projectId: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:CreateProject:Multi-lingual:Select`, {
        projectId,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logToggleChange = (
    featureTitle: TelemetryFeatureTitle,
    pageName: string,
    toggleName: string,
    state: string,
    pivotName?: string,
    projectId?: string
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:Toggle:${toggleName}:${state}`, {
        pivotName,
        projectId,
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logPivotClick = (featureTitle: TelemetryFeatureTitle, pageName: string, pivotName: string, projectId: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:Pivot:${pivotName}`, {
        projectId,
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logFilter = (featureTitle: TelemetryFeatureTitle, pageName: string, filterName: string) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:Filter:${filterName}`, {
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logSelection = (
    featureTitle: TelemetryFeatureTitle,
    pageName: string,
    selectionName: string,
    pivotName?: string,
    projectId?: string
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:${selectionName}:Select`, {
        pivotName,
        projectId,
        pageName,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};
/**
 * Log the smart suggestions accepted / rejected count
 *
 * @param isAccepted Whether the suggestion is accepted or rejected
 * @param data The metadata to be logged
 */
export const logQnaMakerSmartSuggestions = (
    featureTitle: TelemetryFeatureTitle,
    isAccepted: boolean,
    data: {
        projectName: string;
        questions: { qnaId: string; sourceType: string; suggestionsCount: number }[];
    }
) =>
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${isAccepted ? 'SmartSuggestions:accept' : 'SmartSuggestions:reject'}`, {
        ...data,
        serviceName: getServiceNameFromTitle(featureTitle),
        isUserAction: true
    });

export const logButtonClickWithCustomData = (
    featureTitle: TelemetryFeatureTitle,
    pageName: string,
    buttonName: string,
    data: { [key: string]: string | number | boolean }
) => {
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:Button:${buttonName}`, {
        ...data,
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logDTButtonClick = (
    featureTitle: TelemetryFeatureTitle,
    pageName: DTPageName,
    buttonType: string,
    buttonName: string,
    stepName?: DTJobCreationStepName
) => {
    const currentStep = stepName ? `${stepName}:` : '';
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:${currentStep}${buttonType}:${buttonName}`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logRedMessageBarVisible = (featureTitle: TelemetryFeatureTitle, pageName: DTPageName, stepName?: DTJobCreationStepName) => {
    const currentStep = stepName ? `${stepName}:` : '';
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:${currentStep}NoSelectedResource:Header:Visible`, {
        isUserAction: false,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logDTLinkClick = (
    featureTitle: TelemetryFeatureTitle,
    pageName: DTPageName,
    linkName: string,
    stepName?: DTJobCreationStepName
) => {
    const currentStep = stepName ? `${stepName}:` : '';
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:${currentStep}ExternalLink:${linkName}`, {
        isUserAction: true,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};

export const logDTLanguageDropDownSelect = (
    featureTitle: TelemetryFeatureTitle,
    pageName: DTPageName,
    dropdownName: string,
    languageSelected: string,
    stepName?: DTJobCreationStepName
) => {
    const currentStep = stepName ? `${stepName}:` : '';
    getLogger().logEvent(`${getFeatureNameFromTitle(featureTitle)}:${pageName}:${currentStep}dropdown:${dropdownName}:Select`, {
        isUserAction: true,
        languageSelected,
        serviceName: getServiceNameFromTitle(featureTitle)
    });
};
